import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";

export const Route = createFileRoute("/_authenticated/categories")({ component: Page });

const DEFAULT_TPL = "{icon} <b>{title}</b>\n\n{summary}\n\n🏷️ {category}\n🔗 <a href=\"{url}\">อ่านต้นฉบับ</a>";
const empty = { slug: "", name_en: "", name_th: "", icon: "📰", color: "#D4AF37", sort_order: 0, status: true, auto_send_enabled: true, requires_approval: false, telegram_template: "" };

function Page() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [form, setForm] = useState(empty);

  const { data } = useQuery({
    queryKey: ["categories"],
    queryFn: async () => (await supabase.from("asset_categories").select("*").order("sort_order")).data ?? [],
  });

  const save = async () => {
    if (!form.slug || !form.name_th) return toast.error("กรอก slug และชื่อภาษาไทย");
    const op = editing
      ? supabase.from("asset_categories").update(form as any).eq("id", editing.id)
      : supabase.from("asset_categories").insert(form as any);
    const { error } = await op;
    if (error) return toast.error(error.message);
    toast.success("บันทึกสำเร็จ"); setOpen(false);
    qc.invalidateQueries({ queryKey: ["categories"] });
  };
  const toggle = async (id: string, field: string, val: boolean) => {
    const upd: any = { [field]: val };
    const { error } = await (supabase.from("asset_categories") as any).update(upd).eq("id", id);
    if (error) return toast.error(error.message);
    qc.invalidateQueries({ queryKey: ["categories"] });
  };
  const remove = async (id: string) => {
    if (!confirm("ลบ?")) return;
    const { error } = await supabase.from("asset_categories").delete().eq("id", id);
    if (error) return toast.error(error.message);
    qc.invalidateQueries({ queryKey: ["categories"] });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-display font-semibold">Asset Categories</h1>
          <p className="text-sm text-muted-foreground">ตั้งค่าประเภทสินทรัพย์ เปิด/ปิดส่งอัตโนมัติแยกราย Category</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button onClick={() => { setEditing(null); setForm(empty); setOpen(true); }}><Plus className="h-4 w-4 mr-2"/>เพิ่ม Category</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{editing ? "แก้ไข" : "เพิ่ม"} Category</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Slug</Label><Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })}/></div>
                <div className="space-y-2"><Label>Icon</Label><Input value={form.icon} onChange={(e) => setForm({ ...form, icon: e.target.value })}/></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Name EN</Label><Input value={form.name_en} onChange={(e) => setForm({ ...form, name_en: e.target.value })}/></div>
                <div className="space-y-2"><Label>Name TH</Label><Input value={form.name_th} onChange={(e) => setForm({ ...form, name_th: e.target.value })}/></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Color</Label><Input value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })}/></div>
                <div className="space-y-2"><Label>ลำดับ</Label><Input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value) })}/></div>
              </div>
              <div className="flex items-center justify-between"><Label>Auto Send</Label><Switch checked={form.auto_send_enabled} onCheckedChange={(v) => setForm({ ...form, auto_send_enabled: v })}/></div>
              <div className="flex items-center justify-between"><Label>ต้องอนุมัติก่อนส่ง</Label><Switch checked={form.requires_approval} onCheckedChange={(v) => setForm({ ...form, requires_approval: v })}/></div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>เทมเพลตข้อความ Telegram</Label>
                  <Button type="button" size="sm" variant="ghost" onClick={() => setForm({ ...form, telegram_template: DEFAULT_TPL })}>ใช้ค่าเริ่มต้น</Button>
                </div>
                <Textarea
                  rows={6}
                  className="font-mono text-xs"
                  placeholder={DEFAULT_TPL}
                  value={form.telegram_template ?? ""}
                  onChange={(e) => setForm({ ...form, telegram_template: e.target.value })}
                />
                <p className="text-xs text-muted-foreground">
                  ตัวแปร: <code>{"{icon}"}</code> <code>{"{title}"}</code> <code>{"{summary}"}</code> <code>{"{category}"}</code> <code>{"{url}"}</code> <code>{"{tags}"}</code> · รองรับ HTML: &lt;b&gt; &lt;i&gt; &lt;a&gt;
                </p>
              </div>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>ยกเลิก</Button><Button onClick={save}>บันทึก</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader><CardTitle>ประเภทสินทรัพย์</CardTitle><CardDescription>{data?.length ?? 0} รายการ</CardDescription></CardHeader>
        <CardContent>
          <Table>
            <TableHeader><TableRow>
              <TableHead>ลำดับ</TableHead><TableHead>Category</TableHead><TableHead>Slug</TableHead><TableHead>Auto Send</TableHead><TableHead>ต้องอนุมัติ</TableHead><TableHead className="text-right">การกระทำ</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {(data ?? []).map((c: any) => (
                <TableRow key={c.id}>
                  <TableCell>{c.sort_order}</TableCell>
                  <TableCell><span className="text-lg mr-2">{c.icon}</span><span className="font-medium">{c.name_th}</span><span className="text-xs text-muted-foreground ml-2">{c.name_en}</span></TableCell>
                  <TableCell><Badge variant="outline" style={{ borderColor: c.color, color: c.color }}>{c.slug}</Badge></TableCell>
                  <TableCell><Switch checked={c.auto_send_enabled} onCheckedChange={(v) => toggle(c.id, "auto_send_enabled", v)}/></TableCell>
                  <TableCell><Switch checked={c.requires_approval} onCheckedChange={(v) => toggle(c.id, "requires_approval", v)}/></TableCell>
                  <TableCell className="text-right space-x-1">
                    <Button size="sm" variant="ghost" onClick={() => { setEditing(c); setForm(c); setOpen(true); }}><Pencil className="h-3 w-3"/></Button>
                    <Button size="sm" variant="ghost" onClick={() => remove(c.id)}><Trash2 className="h-3 w-3 text-destructive"/></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}